import { AppDispatch, GetState } from '../../shared/services/store';
import { getCaptchaSelector } from './CaptchaSelector';
import { setCaptchaPromiseMethods, setCaptchaValue, setCaptchaExpiresAt } from './CaptchaReducer';
import { defaultErrorMessage } from '../../shared/helpers/fetch-helper';
import { addFlashMessages } from '../../actions/FlashMessagesActions';
import { localeData } from '../../locales/data';
import { CaptchaReturnType } from './Captcha.type';
import { logErrorService } from '../../shared/services';

export const CAPTCHA_RETRY_SYMBOL = Symbol('captcha-retry');
export const CAPTCHA_DEFAULT_VALUE = '';
export const CAPTCHA_EXECUTE_TIMEOUT_MS = 60000;
export const CAPTCHA_EXECUTE_TIMEOUT_ERROR = 'Captcha verification timed out after 60 seconds.';
export const CAPTCHA_WIDGET_MISSING_ERROR = 'Captcha widget is not mounted; skipping verification.';
export const CAPTCHA_CHALLENGE_FETCH_ERROR_KEY = 'captcha.challenge.fetch.error';

export const parseExpiresFromPayload = (payload: string): number | null => {
  try {
    const json = JSON.parse(atob(payload));
    const salt: unknown = json?.salt;
    if (typeof salt !== 'string') return null;
    const match = salt.match(/expires=(\d+)/);
    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
};

// Global ref to the Altcha widget element, set by AltchaWidget component
let altchaWidgetRef: HTMLElement | null = null;

export const setAltchaWidgetRef = (ref: HTMLElement | null) => {
  altchaWidgetRef = ref;
};

export const getAltchaWidgetRef = () => altchaWidgetRef;

// When true, the widget's next 'error' state event was triggered by a
// background refresh (post-consumption pre-compute) rather than a foreground,
// user-initiated verification. The AltchaWidget statechange handler reads
// this flag (via isBackgroundRefreshActive) so it can skip surfacing the
// error to the user and avoid rejecting the next foreground promise.
let backgroundRefreshActive = false;

export const isBackgroundRefreshActive = () => backgroundRefreshActive;

export const setBackgroundRefreshActive = (active: boolean) => {
  backgroundRefreshActive = active;
};

// Source of the most recent verify() call. Set right before invoking the
// widget's verify(), read by the AltchaWidget statechange handler so error
// diagnostics can distinguish which call path was in flight when the failure
// happened.
//   - 'precompute'         — the on-mount 3s timer in AltchaWidget
//   - 'foreground'         — captchaExecuteAsync slow path (user-initiated)
//   - 'background-refresh' — post-consumption pre-compute from the fast path
export type CaptchaVerifySource = 'precompute' | 'foreground' | 'background-refresh';

let verifySource: CaptchaVerifySource | null = null;

export const getVerifySource = () => verifySource;

export const setVerifySource = (source: CaptchaVerifySource | null) => {
  verifySource = source;
};

// True while the widget is mid-PoW (between 'verifying' and a terminal
// state). Read by captchaExecuteAsync so a foreground call can attach to
// an in-flight verify (precompute or background-refresh) instead of
// killing it with reset() + verify(). AltchaWidget keeps this in sync.
let verifyInFlight = false;

export const isVerifyInFlight = () => verifyInFlight;

export const setVerifyInFlight = (active: boolean) => {
  verifyInFlight = active;
};

// The altcha 'error' statechange event drops the error message, so the
// customfetch wrapper records the cause here for captchaError to read.
export interface ChallengeFetchError {
  message: string;
  url: string;
}

let lastChallengeFetchError: ChallengeFetchError | null = null;

export const getChallengeFetchError = () => lastChallengeFetchError;

export const clearChallengeFetchError = () => {
  lastChallengeFetchError = null;
};

// Throws on failure so altcha enters its 'error' state; logging happens once in captchaError.
export const captchaChallengeFetch = async (url: string, init?: RequestInit): Promise<Response> => {
  lastChallengeFetchError = null;

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    lastChallengeFetchError = { message, url };
    throw err;
  }

  if (!response.ok) {
    const message = `Server responded with ${response.status}.`;
    lastChallengeFetchError = { message, url };
    throw new Error(message);
  }

  return response;
};

// Primary way of executing captcha verification.
// Await its dispatch; if it returns CAPTCHA_RETRY_SYMBOL, do early exit in parent functions.
//
// The widget pre-computes PoW on page load so a solution may already be
// available in Redux.  When it is, we return it immediately and clear the
// stored value so the widget can start computing a fresh solution for the
// next call.  If no pre-computed solution exists we fall back to the
// original reset() + verify() flow.
export const captchaExecuteAsync =
  () =>
  async (dispatch: AppDispatch, getState: GetState): Promise<CaptchaReturnType> => {
    const widget = getAltchaWidgetRef();

    if (!widget) {
      // If no widget is mounted, skip captcha and return default value.
      // This is unexpected in production — log to Sentry so we can diagnose.
      logErrorService(CAPTCHA_WIDGET_MISSING_ERROR);
      return CAPTCHA_DEFAULT_VALUE;
    }

    const captcha = getCaptchaSelector(getState());

    // Fast path: a pre-computed solution is already available from the
    // on-mount verify().  Return it immediately and clear the stored value
    // so the widget re-verifies for the next use.
    const preComputedSolution = captcha.value.value;
    const expiresAt = captcha.expiresAt;
    const isExpired = expiresAt !== null && Date.now() / 1000 >= expiresAt;

    if (preComputedSolution && !isExpired) {
      dispatch(setCaptchaValue(null));
      dispatch(setCaptchaExpiresAt(null));
      // Kick off a fresh PoW computation for the next call. Defer to a
      // macrotask so it doesn't race with the in-flight request that's about
      // to consume the value we just returned. Mark the refresh as a
      // background operation so the AltchaWidget statechange handler does
      // not surface its 'error' state as a user-visible flash message or
      // reject any pending foreground promise. Wrap in try/catch so a
      // background refresh failure doesn't leak to the caller — the next
      // captchaExecuteAsync() call will simply take the slow path.
      setBackgroundRefreshActive(true);
      setTimeout(() => {
        try {
          (widget as any).reset?.();
          setVerifySource('background-refresh');
          (widget as any).verify?.();
        } catch (e) {
          logErrorService(e);
          setBackgroundRefreshActive(false);
        }
      }, 0);
      return preComputedSolution;
    }

    // Discard expired pre-computed solution so the widget fetches a fresh one
    if (preComputedSolution) {
      dispatch(setCaptchaValue(null));
      dispatch(setCaptchaExpiresAt(null));
    }

    if (captcha.value.resolve) {
      // If we see a previous pending captcha, resolve it with retry symbol.
      // In the calling code, check for this symbol and do early return.
      captcha.value.resolve(CAPTCHA_RETRY_SYMBOL);
    }

    return new Promise((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const clearExecuteTimeout = () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      };

      const resolveWithCleanup = (value: string | typeof CAPTCHA_RETRY_SYMBOL) => {
        clearExecuteTimeout();
        resolve(value);
      };

      const rejectWithCleanup = (error: string) => {
        clearExecuteTimeout();
        reject(error);
      };

      timeoutId = setTimeout(() => {
        dispatch(setCaptchaValue(null));
        dispatch(setCaptchaExpiresAt(null));
        rejectWithCleanup(CAPTCHA_EXECUTE_TIMEOUT_ERROR);
      }, CAPTCHA_EXECUTE_TIMEOUT_MS);

      dispatch(setCaptchaPromiseMethods({ resolve: resolveWithCleanup, reject: rejectWithCleanup }));

      // If a verify is already in flight (precompute or background-refresh),
      // do NOT call reset() — that would cancel the running PoW worker and
      // surface as "Unexpected result returned." (foreground-vs-precompute
      // race, confirmed in prod telemetry). Instead, attach: when the
      // in-flight verify completes, captchaSuccess will resolve the promise
      // we just registered above.
      if (isVerifyInFlight()) {
        return;
      }

      // Reset and re-trigger the Altcha widget verification
      try {
        // The Altcha web component exposes these methods
        (widget as any).reset?.();
        setVerifySource('foreground');
        // Programmatically trigger the challenge fetch and PoW solving.
        // We do NOT rely on auto="onload" because it doesn't fire when the
        // widget is inside a display:none container.
        (widget as any).verify?.();
      } catch (e) {
        logErrorService(e);
        dispatch(setCaptchaValue(null));
        dispatch(setCaptchaExpiresAt(null));
        defaultErrorMessage(dispatch);
        rejectWithCleanup(String(e));
      }
    });
  };

export const captchaSuccess = (payload: string) => (dispatch: AppDispatch, getState: GetState) => {
  const captcha = getCaptchaSelector(getState());
  // Background refresh succeeded — clear the flag so subsequent verifications
  // are treated as foreground again.
  setBackgroundRefreshActive(false);

  if (captcha.value.resolve) {
    // A foreground caller is waiting on this verify (slow path or attached
    // to an in-flight verify). Resolve it directly — the caller will submit
    // the payload to the server, which marks it consumed. Do NOT also store
    // the payload in state, or a subsequent fast-path call would resubmit
    // the same now-consumed value and the server would reject it as
    // "Incorrect captcha."
    captcha.value.resolve(payload);
    dispatch(setCaptchaValue(null));
    dispatch(setCaptchaExpiresAt(null));
    return;
  }

  // No foreground waiter — this is a precompute or background-refresh
  // result. Store it for the next captchaExecuteAsync to consume via the
  // fast path.
  dispatch(setCaptchaValue(payload));
  const expiresAt = parseExpiresFromPayload(payload);
  dispatch(setCaptchaExpiresAt(expiresAt));
};

export interface CaptchaErrorDiagnostics {
  // Full statechange event detail from altcha (state, message, payload,
  // and any challenge fields the widget exposes). Helps distinguish a
  // boundary failure (server number > maxnumber) from a malformed
  // challenge or a worker bail-out.
  detail?: unknown;
  // PoW duration in ms from 'verifying' to 'error', or null if unknown.
  // Very fast failures (<100ms) suggest the worker bailed without
  // exhausting maxnumber; very slow failures (>5s) point to a CPU /
  // worker scheduling issue on the user's device.
  elapsedMs?: number | null;
  // Which verify() call was in flight when the error fired. Helps separate
  // foreground (user-initiated) failures from races involving the on-mount
  // pre-compute timer or the post-consumption background refresh.
  verifySource?: CaptchaVerifySource | null;
  // Time since AltchaWidget mount in ms. Errors clustered at <3s point to
  // a race with the on-mount pre-compute timer firing into a foreground
  // verify already in flight.
  msSinceMount?: number | null;
  // True if a verify() was already in flight (state === 'verifying') when
  // the most recent verify() was kicked off. Direct evidence of an overlap
  // race between two of the three verify sources.
  verifyOverlap?: boolean;
  // The verify source that was already in flight when the new verify()
  // started, if verifyOverlap is true.
  previousInFlightSource?: CaptchaVerifySource | null;
}

export const captchaError =
  (error: string, diagnostics: CaptchaErrorDiagnostics = {}) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const isBackground = isBackgroundRefreshActive();
    const challengeFetchError = getChallengeFetchError();
    clearChallengeFetchError();

    const logMessage = challengeFetchError ? `Captcha challenge fetch failed: ${challengeFetchError.message}` : error;
    logErrorService(logMessage, { ...diagnostics, isBackgroundRefresh: isBackground, challengeFetchError });

    // If this error came from a background refresh (post-consumption pre-compute
    // kicked off by captchaExecuteAsync), don't surface it to the user and
    // don't reject any pending foreground promise. The next user-initiated
    // captchaExecuteAsync() will simply take the slow path and fetch a fresh
    // challenge.
    if (isBackground) {
      setBackgroundRefreshActive(false);
      dispatch(setCaptchaValue(null));
      dispatch(setCaptchaExpiresAt(null));
      return;
    }

    const captcha = getCaptchaSelector(getState());
    dispatch(setCaptchaValue(null));
    dispatch(setCaptchaExpiresAt(null));
    captcha.value.reject?.(error);

    if (challengeFetchError) {
      dispatch(
        addFlashMessages([{ msg: CAPTCHA_CHALLENGE_FETCH_ERROR_KEY, defaultMessage: localeData.en[CAPTCHA_CHALLENGE_FETCH_ERROR_KEY] }])
      );
    } else {
      defaultErrorMessage(dispatch);
    }
  };

export const resetCaptcha = () => (dispatch: AppDispatch, getState: GetState) => {
  const captcha = getCaptchaSelector(getState());
  captcha.value.reject?.('Captcha verification reset.');
  dispatch(setCaptchaValue(null));
  dispatch(setCaptchaExpiresAt(null));
  const widget = getAltchaWidgetRef();
  if (widget) {
    (widget as any).reset?.();
  }
};
